import { TableCellNode, TableNode } from "@lexical/table";
import { $isRootNode } from "lexical";
import { $findAllNodesOfType } from "../utils/find-all-nodes-of-type";

import type { CellPadding } from "../types/cell-padding";
import type { Border } from "../types/data-table-border";
import type { SerializedTableCellNode, SerializedTableNode } from "@lexical/table";
import type { EditorConfig, LexicalEditor, LexicalNode, NodeKey } from "lexical";

type SerializedDataTableNode = SerializedTableNode & {
  type: "data-table";
  border?: Border;
  cellPadding?: CellPadding;
  backgroundColour?: string;
};

export class DataTableNode extends TableNode {
  __border: Border;
  __cellPadding: CellPadding;
  __backgroundColour?: string;

  constructor(
    border?: Border,
    cellPadding?: CellPadding,
    backgroundColour?: string,
    key?: NodeKey,
  ) {
    super(key);
    this.__border = border ?? { width: 1, style: "solid", colour: "#000" };
    this.__cellPadding = cellPadding ?? {
      setIndividually: false,
      all: 1,
    };
    this.__backgroundColour = backgroundColour;
  }

  static getType(): string {
    return "data-table";
  }

  static clone(node: DataTableNode): DataTableNode {
    return new this(node.__border, node.__cellPadding, node.__backgroundColour, node.__key);
  }

  afterCloneFrom(node: this): void {
    super.afterCloneFrom(node);
    this.__border = node.__border;
    this.__cellPadding = node.__cellPadding;
    this.__backgroundColour = node.__backgroundColour;
  }

  exportJSON(): SerializedDataTableNode {
    return {
      ...super.exportJSON(),
      type: "data-table",
      border: this.__border,
      cellPadding: this.__cellPadding,
      backgroundColour: this.__backgroundColour,
    };
  }

  static importJSON(serializedNode: SerializedDataTableNode): DataTableNode {
    return new DataTableNode(
      serializedNode.border,
      serializedNode.cellPadding,
      serializedNode.backgroundColour,
    ).updateFromJSON(serializedNode);
  }

  createDOM(config: EditorConfig, editor?: LexicalEditor): HTMLElement {
    const dom = super.createDOM(config, editor);
    if (dom instanceof HTMLTableElement) {
      dom.classList.add("data-table");
    } else {
      // Scrollable tables: dom is a wrapper div containing the table
      const table = dom.querySelector("table");
      if (table) {
        table.classList.add("data-table");
      }
    }
    return dom;
  }

  setCellPadding(cellPadding: CellPadding): void {
    const writable = this.getWritable();
    writable.__cellPadding = cellPadding;
    // Mark all cell children as dirty so they update their DOM
    const cellNodes = $findAllNodesOfType(this, $isDataTableCellNode);
    cellNodes.forEach((cellNode) => {
      cellNode.getWritable();
    });
  }

  getCellPadding(): CellPadding {
    return this.__cellPadding;
  }

  getCellPaddingString(): string {
    return this.__cellPadding.setIndividually
      ? `${this.__cellPadding.top}px ${this.__cellPadding.right}px ${this.__cellPadding.bottom}px ${this.__cellPadding.left}px`
      : `${this.__cellPadding.all}px`;
  }

  setCellBackgroundColour(backgroundColour: string): void {
    const writable = this.getWritable();
    writable.__backgroundColour = backgroundColour;
    const cellNodes = $findAllNodesOfType(this, $isDataTableCellNode);
    cellNodes.forEach((cellNode) => {
      cellNode.getWritable().setBackgroundColor(backgroundColour);
    });
  }

  getBackgroundColor(): string {
    return this.__backgroundColour ?? "";
  }

  getBorder(): Border {
    return this.__border;
  }

  setBorder(border: Border): void {
    const writable = this.getWritable();
    writable.__border = border;
    const cellNodes = $findAllNodesOfType(this, $isDataTableCellNode);
    cellNodes.forEach((cellNode) => {
      cellNode.getWritable();
    });
  }

  getBorderString(): string {
    return `${this.__border.width}px ${this.__border.style} ${this.__border.colour}`;
  }
}

export function $createDataTableNode(border?: Border, cellPadding?: CellPadding): DataTableNode {
  return new DataTableNode(border, cellPadding);
}

export function $isDataTableNode(node: LexicalNode | null | undefined): node is DataTableNode {
  return node instanceof DataTableNode;
}

export const findDataTableNode = (cellNode: LexicalNode): DataTableNode | null => {
  if ($isDataTableNode(cellNode)) {
    return cellNode;
  }

  const parent = cellNode.getParent();
  if (!parent || $isRootNode(parent)) return null;

  return findDataTableNode(parent);
};

type SerializedDataTableCellNode = SerializedTableCellNode & {
  type: "data-table-cell";
  border?: string;
};

export class DataTableCellNode extends TableCellNode {
  static getType(): string {
    return "data-table-cell";
  }

  constructor(backgroundColor?: string, key?: NodeKey) {
    super(undefined, undefined, undefined, key);
    this.__backgroundColor = backgroundColor ?? null;
  }

  static clone(node: DataTableCellNode): DataTableCellNode {
    return new this(node.__backgroundColor ?? undefined, node.__key);
  }

  exportJSON(): SerializedDataTableCellNode {
    return { ...super.exportJSON(), type: "data-table-cell" };
  }

  static importJSON(serializedNode: SerializedDataTableCellNode): DataTableCellNode {
    return new this(serializedNode.backgroundColor ?? undefined).updateFromJSON(serializedNode);
  }

  createDOM(config: EditorConfig): HTMLTableCellElement {
    const dom = super.createDOM(config);
    dom.classList.add("data-table-cell");

    dom.style.backgroundColor = this.__backgroundColor ?? "transparent";
    const tableNode = findDataTableNode(this);
    if (tableNode) {
      dom.style.border = tableNode.getBorderString();
      dom.style.padding = tableNode.getCellPaddingString();
    }
    return dom;
  }

  updateDOM(): boolean {
    return true;
  }
}

export function $createDataTableCellNode(): DataTableCellNode {
  return new DataTableCellNode();
}

export function $isDataTableCellNode(
  node: LexicalNode | null | undefined,
): node is DataTableCellNode {
  return node instanceof DataTableCellNode;
}
